import React, { MouseEvent, ReactNode, useEffect, useRef } from "react";
import { createUseStyles } from "react-jss";
import { Link, useHistory } from "react-router-dom";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import clsx from "clsx";
import { CircularProgress } from "@mui/material";
import { theme } from "$utils/theme/bataillog";
import TablePagination from "./TablePagination";
import Thead from "./Thead";
import Text from "../Text";
import usePersistedState from "../../hooks/usePersistedState";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  style?: any;
  rowStyle?: any;
  align?: "right";
  format?: (value: number) => string;
  bulle?: string;
  onClickAction?: any;
}

interface Data {
  value: string;
  isDisable?: boolean;
  customCellClass?: string;
  containsChevron?: boolean;
  components?: ReactNode;
  color?: string;
  type?: string;
  link?: string;
  onClickAction?: any;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export type CustomTableProps = {
  rowsPerPage: number;
  thead: Array<any>;
  bodyData: Array<any>;
  className?: string;
  renderItem?: (item: any) => Data[];
  load?: boolean;
  bodyMissingLabelText?: string;
  isRowRelative?: boolean;
  refreshPage?: boolean;
  callback?: any;
  noPointer?: boolean;
  noHover?: boolean;
  pageName?: string;
  usePaper?: boolean;
};

function CustomPaginationActionsTable({
  rowsPerPage,
  thead,
  bodyData,
  renderItem,
  className = "",
  load = false,
  bodyMissingLabelText,
  isRowRelative = false,
  refreshPage = false,
  callback,
  noPointer,
  noHover,
  pageName = "default",
  usePaper = true,
}: CustomTableProps) {
  const history = useHistory();
  const classes = useStyles();
  const [persistedPagination, setPersistedPagination] = usePersistedState(
    0,
    `${pageName}Page-pagination`,
    null
  );
  const refContainer = useRef<HTMLDivElement>(null);

  const dataLength = bodyData?.length;
  // const emptyRows = rowsPerPage - Math.min(rowsPerPage, dataLength - persistedPagination * rowsPerPage);

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPersistedPagination(newPage);
  };

  enum DataRowType {
    Link = "link",
    Dopdown = "dropdown",
  }

  const renderswitch = (type: any, value: string, link?: string) => {
    switch (type) {
      case DataRowType.Link:
        return link ? (
          <Link className={classes.tableLink} to={link}>
            {value}
          </Link>
        ) : null;
      default:
        return value;
    }
  };

  useEffect(() => {
    const noscroll =
      persistedPagination !== 0 &&
      !history.location.pathname.includes("catalogue");
    if (noscroll && refContainer?.current?.parentElement) {
      refContainer.current.parentElement.scrollIntoView({
        behavior: "auto",
        block: "start",
      });
    }
    let pageCount = dataLength / rowsPerPage;
    if (!Number.isInteger(pageCount)) {
      pageCount = Math.floor(pageCount + 1);
    }
    if (persistedPagination > pageCount - 1 && pageCount > 0)
      setPersistedPagination(pageCount - 1);
  }, [
    dataLength,
    history.location.pathname,
    persistedPagination,
    setPersistedPagination,
    rowsPerPage,
  ]);

  useEffect(() => {
    if (refreshPage) {
      setPersistedPagination(0);
      if (callback) {
        callback();
      }
    }
  }, [refreshPage, bodyData, callback, setPersistedPagination]);

  const handleMouseEnter = (
    event: MouseEvent<HTMLTableCellElement>,
    onMouseEnter: () => void
  ) => {
    if (onMouseEnter) {
      onMouseEnter();
    }
  };

  const handleMouseLeave = (event: MouseEvent, onMouseLeave: () => void) => {
    if (onMouseLeave) {
      onMouseLeave();
    }
  };

  return (
    <TableContainer
      className={clsx(classes.tableContainer, className)}
      component={usePaper ? Paper : undefined}
      ref={refContainer}
    >
      {!load && (
        <Table className={classes.table} aria-label="custom pagination table">
          <Thead>
            {thead?.map((column: Column) => (
              <TableCell
                key={column?.id}
                align={column?.align}
                className={classes.tableCell}
                style={{
                  minWidth: column?.minWidth || undefined,
                  maxWidth: column?.maxWidth || undefined,
                  width: column?.width || undefined,
                  ...((() => (column?.style ? column.style : {}))() as any),
                }}
                padding="none"
              >
                <div className={classes.tableCellIn}>{column?.label}</div>
              </TableCell>
            ))}
          </Thead>
          <TableBody>
            {(rowsPerPage > 0
              ? bodyData?.slice(
                  persistedPagination * rowsPerPage,
                  persistedPagination * rowsPerPage + rowsPerPage
                )
              : bodyData
            )?.map((rawRow: Array<Data>, idx: number) => {
              const row = renderItem ? renderItem(rawRow) : rawRow;
              return (
                <TableRow
                  className={clsx(
                    classes.TableRow,
                    noPointer ? "nopointer" : "",
                    isRowRelative ? classes.relative : null
                  )}
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  hover={!noHover}
                >
                  {thead?.map((column: Column, index) => {
                    if (!row[index]) return null;
                    const {
                      value,
                      isDisable,
                      color,
                      type,
                      link,
                      customCellClass,
                      onClickAction,
                      onMouseEnter,
                      onMouseLeave,
                    } = row[index];
                    return (
                      <TableCell
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        onMouseEnter={(
                          event: MouseEvent<HTMLTableCellElement>
                        ) => handleMouseEnter(event, onMouseEnter)}
                        onMouseLeave={(
                          event: MouseEvent<HTMLTableCellElement>
                        ) => handleMouseLeave(event, onMouseLeave)}
                        scope="row"
                        onClick={onClickAction || undefined}
                        align={column?.align}
                        className={clsx(
                          isDisable ? "disabled" : "",
                          classes.tableCell,
                          customCellClass || ""
                        )}
                        style={{
                          minWidth: column?.minWidth || undefined,
                          maxWidth: column?.maxWidth || undefined,
                          width: column?.width || undefined,
                          color,
                          ...((() =>
                            column?.rowStyle ? column.rowStyle : {})() as any),
                        }}
                        padding="none"
                      >
                        <div className={classes.tableCellIn}>
                          {renderswitch(
                            type,
                            column?.format && typeof value === "number"
                              ? column.format(value)
                              : value,
                            link
                          )}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
          {rowsPerPage < dataLength && (
            <TableFooter className={classes.tableFooter}>
              <TableRow>
                <TableCell colSpan={100} className={classes.tableCell}>
                  <TablePagination
                    rowsPerPageOptions={[]}
                    labelDisplayedRows={() => ""}
                    colSpan={10}
                    count={dataLength}
                    rowsPerPage={rowsPerPage}
                    page={persistedPagination}
                    onChangePage={handleChangePage}
                    onPageChange={handleChangePage}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      )}
      {load && (
        <div className={classes.CircularProgress}>
          <CircularProgress color="inherit" />
        </div>
      )}
      {!dataLength && bodyMissingLabelText && (
        <div className={classes.BodyMissingTextContainer}>
          <Text type="secondary">{bodyMissingLabelText || ""}</Text>
        </div>
      )}
    </TableContainer>
  );
}

const useStyles = createUseStyles(() => ({
  tableContainer: {
    padding: theme.spacing(2),
    width: "auto !important",
    overflowX: "auto !important",
    "& td.disabled, & td.disabled a": {
      color: "#B3BAC5 !important",
    },
    flex: "1",
  },
  table: {
    minWidth: 650,
  },
  tableLink: {
    color: "#030F85",
    fontWeight: "bold",
    textDecoration: "none",
  },
  tableFooter: {
    position: "sticky",
    left: 0,
    bottom: 0,
    backgroundColor: "white",
    "& td": {
      border: "none !important",
      paddingBottom: 7,
      paddingtop: 20,
      paddingLeft: 0,
    },
    "& tr": {
      border: "none",
    },
  },
  tableCell: {
    "&.MuiTableCell-root": {
      fontFamily: theme.font.workSans,
      // wordBreak: "break-word",
    },
  },
  tableCellIn: {
    padding: "7px 12px",
    minHeight: 30,
    display: "flex",
    alignItems: "center",
    textAlign: "left",
    // width: "100%",
  },
  CircularProgress: {
    display: "flex",
    justifyContent: "center",
    marginTop: 10,
  },
  BodyMissingTextContainer: {
    textAlign: "center",
    padding: "15px",
  },
  TableRow: {
    cursor: "pointer",
    "&.nopointer": {
      cursor: "default !important",
    },
  },
  relative: {
    position: "relative",
  },
}));

export default CustomPaginationActionsTable;
